import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { api } from "./api";

/**
 * What the Download page and the home page both need: the published installers
 * (`/api/downloads`, written by scripts/publish-desktop.mjs) and a best guess at
 * the visitor's system, so the right button comes first.
 */
export type Platform = "windows" | "mac" | "linux";
export type Arch = "x64" | "arm64";

export interface DownloadFile {
  name: string;
  platform: Platform;
  arch: Arch;
  kind: string;
  size: number;
  sha256: string;
  url: string;
}

export interface Manifest {
  version: string | null;
  releasedAt: string | null;
  signed: boolean;
  files: DownloadFile[];
}

export const PLATFORM_NAME: Record<Platform, string> = { windows: "Windows", mac: "macOS", linux: "Linux" };
export const KIND_NAME: Record<string, string> = {
  exe: "Installer (.exe)",
  dmg: "Disk image (.dmg)",
  zip: "App in a .zip",
  AppImage: "AppImage (any distribution)",
  deb: ".deb (Debian, Ubuntu, Mint)",
};
export const ARCH_NAME: Record<string, string> = {
  x64: "Intel / AMD 64-bit",
  arm64: "Apple silicon (M1 and later)",
};

export type Browser = "ios" | "android" | "chromium" | "firefox" | "safari" | "other";

export interface Detected {
  platform: Platform | null;
  arch: Arch | null;
  browser: Browser;
}

/** Best guess at the visitor's system, from what the browser tells. */
export function detect(): Detected {
  const ua = navigator.userAgent;
  const ios = /iPhone|iPad|iPod/.test(ua) || (ua.includes("Macintosh") && navigator.maxTouchPoints > 1);
  const android = /Android/.test(ua);
  const platform: Platform | null =
    ios || android
      ? null
      : /Windows/.test(ua)
        ? "windows"
        : /Mac/.test(ua)
          ? "mac"
          : /Linux|X11/.test(ua)
            ? "linux"
            : null;
  const browser: Browser = ios
    ? "ios"
    : android
      ? "android"
      : /Firefox\//.test(ua)
        ? "firefox"
        : /Edg\/|Chrome\//.test(ua)
          ? "chromium"
          : /Safari\//.test(ua)
            ? "safari"
            : "other";
  return { platform, arch: platform === "mac" ? null : platform ? "x64" : null, browser };
}

export function mb(bytes: number): string {
  return `${Math.round(bytes / 1e6)} MB`;
}

/** The published installers. */
export function useDownloads() {
  return useQuery({
    queryKey: ["desktop-downloads"],
    queryFn: () => api<Manifest>("/downloads"),
    staleTime: 300_000,
  });
}

/** The visitor's system; on a Mac in Chromium, also whether it is Apple silicon. */
export function useDetectedSystem(): Detected {
  const [d, setD] = useState<Detected>({ platform: null, arch: null, browser: "other" });
  useEffect(() => {
    const found = detect();
    setD(found);
    // Chromium can say whether this Mac is Apple silicon; Safari and Firefox cannot.
    const uad = (
      navigator as Navigator & {
        userAgentData?: { getHighEntropyValues(h: string[]): Promise<{ architecture?: string }> };
      }
    ).userAgentData;
    if (found.platform === "mac" && uad)
      void uad.getHighEntropyValues(["architecture"]).then((v) => {
        if (v.architecture === "arm") setD((x) => ({ ...x, arch: "arm64" }));
        else if (v.architecture === "x86") setD((x) => ({ ...x, arch: "x64" }));
      });
  }, []);
  return d;
}

/** The installers that suit this system, best first (no .zip: the .dmg is the Mac default). */
export function filesFor(files: DownloadFile[], d: Detected): DownloadFile[] {
  if (!d.platform) return [];
  return files.filter((f) => f.platform === d.platform && (!d.arch || f.arch === d.arch) && f.kind !== "zip");
}
